/**
 * Shopify reads. Module-private async fns returned from a factory; each unwraps
 * the BE `{ data: T }` envelope and throws ApiError on failure. Status always
 * returns 200 with a `connected` boolean (no 404 for a not-yet-connected brand).
 */
import { apiClient } from '@discovr/common';
import type {
  ShopifyConversionsParams,
  ShopifyConversionsRange,
  ShopifyConversionsSeries,
  ShopifyConversionsSummary,
  ShopifyCouponMetrics,
  ShopifyDiscountCampaigns,
  ShopifyGlance,
  ShopifyRefusedOrdersPage,
  ShopifyRefusedParams,
  ShopifyStatus,
  ShopifyStoreDiscountFilters,
  ShopifyStoreDiscountsPage,
  ShopifyUninstallImpact,
  ShopifyStoreSummary,
} from '@discovr/types';
import type { ShopifyClaimPreview } from '@discovr/types';
import { INTEGRATION_BASE, getData, getPaged, pagedQs, postData, type PagedRows } from './shared';

const getStatus = (): Promise<ShopifyStatus> =>
  getData<ShopifyStatus>(`${INTEGRATION_BASE}/status`);

const getSummary = (): Promise<ShopifyStoreSummary> =>
  getData<ShopifyStoreSummary>(`${INTEGRATION_BASE}/summary`);

const getGlance = (): Promise<ShopifyGlance> =>
  getData<ShopifyGlance>(`${INTEGRATION_BASE}/glance`);

const conversionsUrl = (campaignId: string) =>
  `/campaigns/${encodeURIComponent(campaignId)}/shopify/conversions`;

const getConversionsPage = <T extends ShopifyCouponMetrics>(
  campaignId: string,
  params: ShopifyConversionsParams,
): Promise<PagedRows<T>> =>
  getPaged<T>(`${conversionsUrl(campaignId)}?${pagedQs({ ...params })}`);

const withRange = (url: string, range: ShopifyConversionsRange): string => {
  const qs = pagedQs({ from: range.from, to: range.to });
  return qs ? `${url}?${qs}` : url;
};

const getConversionsSummary = (
  campaignId: string,
  range: ShopifyConversionsRange = {},
): Promise<ShopifyConversionsSummary> =>
  getData<ShopifyConversionsSummary>(withRange(`${conversionsUrl(campaignId)}/summary`, range));

const getRefusedOrders = (
  campaignId: string,
  params: ShopifyRefusedParams,
): Promise<ShopifyRefusedOrdersPage> =>
  getData<ShopifyRefusedOrdersPage>(`${conversionsUrl(campaignId)}/refused?${pagedQs({ ...params })}`);

const getConversionsSeries = (
  campaignId: string,
  range: ShopifyConversionsRange = {},
): Promise<ShopifyConversionsSeries> =>
  getData<ShopifyConversionsSeries>(withRange(`${conversionsUrl(campaignId)}/series`, range));

const getUninstallImpact = (): Promise<ShopifyUninstallImpact> =>
  getData<ShopifyUninstallImpact>(`${INTEGRATION_BASE}/uninstall-impact`);

export const STORE_DISCOUNTS_PAGE_SIZE = 25;

const getStoreDiscounts = (
  cursor?: string,
  filters: ShopifyStoreDiscountFilters = {},
): Promise<ShopifyStoreDiscountsPage> =>
  getData<ShopifyStoreDiscountsPage>(
    `${INTEGRATION_BASE}/discounts?${pagedQs({
      cursor,
      limit: STORE_DISCOUNTS_PAGE_SIZE,
      status: filters.status,
      type: filters.type,
      method: filters.method,
      source: filters.source,
      campaign_id: filters.campaign_id,
      q: filters.q?.trim(),
    })}`,
  );

const getStoreDiscountCampaigns = (): Promise<ShopifyDiscountCampaigns> =>
  getData<ShopifyDiscountCampaigns>(`${INTEGRATION_BASE}/discounts/campaigns`);

export interface ShopifyCsvExport {
  blob: Blob;
  filename: string;
}

/** Authenticated CSV download. Omit `campaignId` for every campaign of the connected store
 *  (always all time). `range` narrows a single campaign's export to inclusive UTC days. */
const exportConversions = async (
  campaignId?: string,
  range: ShopifyConversionsRange = {},
): Promise<ShopifyCsvExport> => {
  const url = campaignId
    ? withRange(`${conversionsUrl(campaignId)}/export`, range)
    : `${INTEGRATION_BASE}/conversions/export`;
  const res = await apiClient.get<Blob>(url, { responseType: 'blob', timeout: 120_000 });
  const disposition = String(res.headers?.['content-disposition'] ?? '');
  const name =
    /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1] ??
    /filename="?([^";]+)"?/i.exec(disposition)?.[1];
  let filename = campaignId ? 'shopify-sales.csv' : 'shopify-sales-all-campaigns.csv';
  if (name) {
    try {
      filename = decodeURIComponent(name);
    } catch {
      filename = name;
    }
  }
  return { blob: res.data, filename };
};

/** Name the store a claim token would bind, WITHOUT binding it. Backs the
 *  confirmation step: the claim link is a bearer credential, so the merchant
 *  must see which store it connects before anything is committed.
 *
 *  POST despite being a read, and the only read in this file that is: the token
 *  IS the credential, so a query string would put it in every ALB/CDN access
 *  log and request-URL trace — exactly what keeping it in the URL fragment is
 *  for. Same body shape as claimInstall. */
const previewClaim = (token: string): Promise<ShopifyClaimPreview> =>
  postData<ShopifyClaimPreview>(`${INTEGRATION_BASE}/claim/preview`, { claim_token: token });

export function createShopifyQueriesApi() {
  return {
    previewClaim,
    getStatus,
    getSummary,
    getGlance,
    getConversionsPage,
    getConversionsSummary,
    getConversionsSeries,
    getRefusedOrders,
    getUninstallImpact,
    getStoreDiscounts,
    getStoreDiscountCampaigns,
    exportConversions,
  };
}

export type ShopifyQueriesApi = ReturnType<typeof createShopifyQueriesApi>;
